import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'antd';
import { pdf } from '@react-pdf/renderer';

import { convertBlobToBase64, openPrinterWithUrl } from '@/utils/file';
import ConfirmationModal from '@/views/components/Modals/ConfirmationModal';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { useGetOverChargePrintForm } from '@/views/screens/Claims/hooks';

import { downloadBlobFile } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import Button from '../../../../../../components/Buttons/Button';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import OverChargeClaimDetailsPrint from '../../../../../Print/Claims/Overcharge/OverChargeClaimDetailsPrint';
import { useSendOCClaimPdfToCarrier } from '../../../hooks/use-send-oc-claim-pdf-to-carrier';
import OverChargeDetailsPrintModal from '../../../Print/OverChargeDetailsPrintModal';

const OverChargeDrawerHeader = ({ claimDetails, claimNo, currency }) => {
  const { pathname: activeTab, state: locationState } = useLocation();
  const navigate = useNavigate();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const { isPending: isSendPdfToCarrierPending, mutate: sendPdfToCarrier } = useSendOCClaimPdfToCarrier();
  const [printModal, setPrintModal] = useState(false);

  const {
    data: claimPrintDetails,
    isFetching: isClaimPrintDetailsLoading,
    refetch: refetchPrintDetails,
  } = useGetOverChargePrintForm({
    enabled: false,
    FBOCKey: claimDetails?.fbOcKey,
  });

  const shouldShowEmailPrompt =
    Number(locationState?.showEmailPromptForFbOcKey) > 0 &&
    Number(locationState?.showEmailPromptForFbOcKey) === Number(claimDetails?.fbOcKey);

  const handleDefaultEmailSendDialogueCancel = () => {
    navigate(activeTab, { replace: true });
  };

  const generatePDF = async (data, sendEmail) => {
    try {
      const { data: refreshedData } = await refetchPrintDetails();
      const printData = refreshedData ?? data;

      if (!printData) {
        showNotification({
          message: t('translation:default.fields.printError'),
          type: NOTIFICATION_TYPES.ERROR,
        });
        return;
      }

      const fileName = 'OC Claim Details';
      const blob = await pdf(<OverChargeClaimDetailsPrint claimDetails={printData} namespace={namespace} />).toBlob();

      if (sendEmail) {
        const fileBase64 = await convertBlobToBase64(blob);

        const handleDone = () => {
          setPrintModal(false);
          downloadBlobFile(blob, fileName);
        };

        const payload = {
          attachment: String(fileBase64).split(',')[1],
          carrierKey: claimDetails?.carrierKey,
          customEmailAddress: null,
          fbocKey: claimDetails?.fbOcKey,
          handleDone,
          isCustom: 0,
          self: 1,
        };
        sendPdfToCarrier(payload);
      } else {
        downloadBlobFile(blob, fileName);
      }
    } catch {
      showNotification({
        message: t('translation:default.fields.printError'),
        type: NOTIFICATION_TYPES.ERROR,
      });
    }
  };

  const handlePrint = async () => {
    let pdfUrl = '';
    try {
      const { data: refreshedData } = await refetchPrintDetails();
      const printData = refreshedData ?? claimPrintDetails;

      if (!printData) {
        showNotification({
          message: t('translation:default.fields.printError'),
          type: NOTIFICATION_TYPES.ERROR,
        });
        return;
      }

      const blob = await pdf(<OverChargeClaimDetailsPrint claimDetails={printData} namespace={namespace} />).toBlob();
      pdfUrl = URL.createObjectURL(blob);
      await openPrinterWithUrl(pdfUrl, 'oc_claims-pdf-print-iframe');
    } catch {
      showNotification({
        message: t('translation:default.fields.printError'),
        type: NOTIFICATION_TYPES.ERROR,
      });
    } finally {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    }
  };

  const openPrintModal = () => {
    refetchPrintDetails();
    setPrintModal(true);
  };

  return (
    <Row className="flex-nowrap" gutter={30} align="middle">
      <Col className="flex-1">
        <Row className="flex-nowrap" gutter={30} align="middle">
          <Col>
            <Row align="middle" gutter={5}>
              <Col>
                <SecondaryLabel className="f-14  text-white fw-600">
                  {t('translation:default.fields.claimNumber')} :
                </SecondaryLabel>
              </Col>
              <Col>
                <SecondaryLabel className="f-16 text-white fw-600">{claimNo}</SecondaryLabel>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row align="middle" gutter={5}>
              <Col>
                <SecondaryLabel className="f-14  text-white fw-600">
                  {t('translation:default.fields.currency.label')} :
                </SecondaryLabel>
              </Col>
              <Col>
                <SecondaryLabel className="f-16 text-white fw-600">{currency}</SecondaryLabel>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      <Col className="pr-30">
        <Row gutter={15}>
          <Col>
            <Button type="default" invertMode onClick={openPrintModal}>
              Print Form
            </Button>
          </Col>
          <Col>
            <Button loading={isSendPdfToCarrierPending} onClick={() => generatePDF(claimPrintDetails, true)} invertMode>
              {t('translation:claims.emailToTransportationProvider')}
            </Button>
          </Col>
        </Row>
      </Col>
      <OverChargeDetailsPrintModal
        claimDetails={claimPrintDetails ? claimPrintDetails : {}}
        open={printModal}
        setOpen={setPrintModal}
        isLoading={isClaimPrintDetailsLoading}
        generatePDF={generatePDF}
        handlePrint={handlePrint}
      />
      <ConfirmationModal
        className="w-auto"
        title="Alert"
        open={shouldShowEmailPrompt}
        description={
          <>
            {t('translation:claims.successFullyCreated')}. {t('translation:default.fields.claimNumber')} : {claimNo}{' '}
            <br />
            {t('translation:claims.sendEmailToTransportationProviderConfirmation')}
          </>
        }
        onOk={() => {
          generatePDF(claimPrintDetails, true);
          handleDefaultEmailSendDialogueCancel();
        }}
        onClose={handleDefaultEmailSendDialogueCancel}
        onCancel={handleDefaultEmailSendDialogueCancel}
      />
    </Row>
  );
};

export default OverChargeDrawerHeader;
